import { useCallback, useEffect, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { useLocation } from 'react-router-dom';
import {
  EventAccordionItemProps,
  InventoryEventsAccordion,
  translateEventToInventoryAccordionItem,
} from 'src/components/Accordions';
import * as EmptySectionContent from 'src/components/common/EmptySectionContent';
import { MultiSelectActionBar } from 'src/components/common/MultiSelect/MultiSelectActionBar';
import { SetInventoryEventAccordionSelectionCountLabel } from 'src/components/Events/EventListing/InventoryEventListing/SetInventoryEventAccordionSelectionCountLabel';
import { SetInventoryMetricsSelectionCountLabel } from 'src/components/Events/EventListing/InventoryEventListing/SetInventoryMetricsViewSelectionCountLabel';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { Content } from 'src/contexts/ContentContext';
import { EventHubContextProvider } from 'src/contexts/EventHubContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import {
  NO_GROUP_ID,
  useMultiSelectionContext,
} from 'src/contexts/MultiSelectionContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { useScrollToEventIndexFromBackSection } from 'src/hooks/useScrollToEventIndexFromBackSection';
import { useUserHasConsignorMasterUI } from 'src/hooks/useUserIsConsignorMasterUI';
import { SearchSolidIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { SomethingWentWrong } from 'src/views';
import { InventoryViewMode, Listing, ListingQuery } from 'src/WebApiController';

import * as styles from '../EventListing.css';
import { InventoryGlobalBulkActions } from './BulkActions';
import { InventoryDetailedEventView } from './InventoryDetailedEventView';
import { InventoryEventSummaryMetrics } from './InventoryEventSummaryMetrics';
import { InventoryFlattenedView } from './InventoryFlattenedView';

export function InventoryEventListing({
  onItemsRendered,
}: {
  onItemsRendered?: (items: EventAccordionItemProps<Listing>[]) => void;
}) {
  const {
    isLoading,
    errorInfo,
    eventsTransformed,
    eventsExpansion: { expandedListItems, toggleListItemExpansion },
  } = useCatalogDataContext();
  const { filterQuery } = useFilterQueryContext<ListingQuery>();
  const location = useLocation();

  const hasConsignorMasterUI = useUserHasConsignorMasterUI();

  const onInventoryEventExpanded = useCallback(
    async (viagVirtId: string) => {
      toggleListItemExpansion([viagVirtId]);
    },
    [toggleListItemExpansion]
  );

  const { returningFromViagVirtId, scrollToItemIndex } =
    useScrollToEventIndexFromBackSection();

  const eventIdFromHash =
    location.hash.length === 37 ? location.hash.substring(1) : undefined; // UUID 36 chars

  useEffect(() => {
    if (eventIdFromHash) {
      toggleListItemExpansion([eventIdFromHash]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventIdFromHash]);

  const {
    allGroupsIds,
    setAllGroupsIds,
    clearShiftKeySelection,
    setSelectionMode,
  } = useMultiSelectionContext();

  useEffect(() => {
    clearShiftKeySelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterQuery.viewMode]);

  const listingItems = useMemo(() => {
    return (
      eventsTransformed?.map((e, i) =>
        translateEventToInventoryAccordionItem(e, i)
      ) || []
    );
  }, [eventsTransformed]);

  useEffect(() => {
    // Important useEffect to keep allGroupIds in sync with the items
    // This is important for the multi select to work properly
    if (filterQuery.viewMode === InventoryViewMode.FlattenedView) {
      if (!isEqual(allGroupsIds, [NO_GROUP_ID])) {
        setAllGroupsIds([NO_GROUP_ID]);
      }
    } else {
      const groupIds = listingItems.map((listingItem) => listingItem.itemId);
      if (!isEqual(allGroupsIds, groupIds)) {
        setAllGroupsIds(groupIds);
      }
    }
  }, [listingItems, filterQuery.viewMode, allGroupsIds, setAllGroupsIds]);

  // Reset and close selection mode when changing view
  useEffect(() => {
    setSelectionMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterQuery.viewMode]);

  return (
    <EventHubContextProvider>
      {isLoading && <PosSpinner />}
      {errorInfo && (
        <SomethingWentWrong
          header={errorInfo.errorHeader}
          message={<Content id={ContentId.FailToLoadListContent} />}
        />
      )}
      {!isLoading &&
        !errorInfo &&
        (eventsTransformed && eventsTransformed.length > 0 ? (
          <div className={styles.eventsListContainer}>
            {filterQuery.viewMode === InventoryViewMode.MetricView ? (
              <InventoryDetailedEventView
                before={
                  !hasConsignorMasterUI && <InventoryEventSummaryMetrics />
                }
                topListItemBefore={
                  <SetInventoryMetricsSelectionCountLabel
                    events={eventsTransformed}
                  >
                    <MultiSelectActionBar>
                      <InventoryGlobalBulkActions />
                    </MultiSelectActionBar>
                  </SetInventoryMetricsSelectionCountLabel>
                }
                items={eventsTransformed}
                highlightEvent={eventIdFromHash}
                scrollToEventVirtId={returningFromViagVirtId}
              />
            ) : filterQuery.viewMode === InventoryViewMode.FlattenedView ? (
              <InventoryFlattenedView
                before={
                  !hasConsignorMasterUI && <InventoryEventSummaryMetrics />
                }
                enableColumnFilters
              />
            ) : (
              <InventoryEventsAccordion
                before={
                  !hasConsignorMasterUI && <InventoryEventSummaryMetrics />
                }
                topListItemBefore={
                  <SetInventoryEventAccordionSelectionCountLabel>
                    <MultiSelectActionBar>
                      <InventoryGlobalBulkActions />
                    </MultiSelectActionBar>
                  </SetInventoryEventAccordionSelectionCountLabel>
                }
                items={listingItems}
                disablePagination
                scrollToItemIndex={scrollToItemIndex}
                selectedIds={expandedListItems}
                onSelect={onInventoryEventExpanded}
                onItemsRendered={onItemsRendered}
              />
            )}
          </div>
        ) : (
          <EmptySectionContent.Root
            icon={
              <EmptySectionContent.SolidIconContainer>
                <SearchSolidIcon />
              </EmptySectionContent.SolidIconContainer>
            }
          >
            <EmptySectionContent.Label>
              <Content id={ContentId.NoResultFound} />
            </EmptySectionContent.Label>
            <EmptySectionContent.DetailMessage>
              <Content id={ContentId.NoEventsMatching} />
            </EmptySectionContent.DetailMessage>
          </EmptySectionContent.Root>
        ))}
    </EventHubContextProvider>
  );
}
